import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode
} from 'react'
import type { LucideIcon } from 'lucide-react'

interface SelfServiceContextValue {
  /**
   * Register a back handler for the current flow.
   * Returns a cleanup function to unregister.
   */
  registerBackHandler: (handler: () => void) => () => void
  /**
   * Call the registered back handler, or exit flow if none registered
   */
  handleBack: () => void
  /**
   * Whether a flow is currently active (has registered a back handler)
   */
  hasActiveFlow: boolean
  /**
   * Hide the back button entirely (for terminal states)
   */
  setBackHidden: (hidden: boolean) => void
  /**
   * Whether the back button should be hidden
   */
  isBackHidden: boolean
  /**
   * Current step progress for the global footer stepper, or null to hide it
   * (hub, emergency, terminal/loading states). Published by the active flow.
   */
  stepProgress: StepProgress | null
  /**
   * Publish the active flow's step progress to the global footer stepper.
   * Pass null to hide the progress (e.g. emergency, terminal states).
   */
  setStepProgress: (progress: StepProgress | null) => void
  /**
   * Title of the current view, shown in the global header (in place of the
   * logo/product/practice name). Published by the active step's StepHeader;
   * null on the hub and on hero/done screens that carry their own heading.
   */
  viewTitle: ViewTitle | null
  /** Publish the current view title to the global header. Pass null to clear. */
  setViewTitle: (title: ViewTitle | null) => void
  /** External input currently required by the mounted self-service step. */
  externalInput: SelfServiceExternalInput | null
  /** Publish or clear the external input required by the mounted step. */
  setExternalInput: (input: SelfServiceExternalInput | null) => void
  /** Whether the mounted card step is waiting for its bounded read operation. */
  cardReadPending: boolean
  setCardReadPending: (pending: boolean) => void
  /** Whether a terminal step owns the shell timeout under a bounded lease. */
  terminalSessionLeaseActive: boolean
  setTerminalSessionLeaseActive: (active: boolean) => void
  /** Primary action of the mounted step, rendered on the right of the footer. */
  primaryAction: PublishedStepAction | null
  /** Secondary action of the mounted step, rendered left of the primary action. */
  secondaryAction: PublishedStepAction | null
  setPrimaryAction: (update: StepActionUpdate) => void
  setSecondaryAction: (update: StepActionUpdate) => void
}

/**
 * Visual tone of a footer step action. `primary` is the default forward
 * action; `success` finishes a flow; `destructive` cancels or declines.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const tone: StepActionTone = 'primary'
 * ```
 */
export type StepActionTone = 'primary' | 'success' | 'destructive'

/**
 * A step action published to the shell footer by {@link useStepPrimaryAction}
 * or {@link useStepSecondaryAction}. Every action carries an icon; the label
 * uses the shared vocabulary (`common.next`, `common.done`, `common.confirm`,
 * …) so the forward action reads the same on every step.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const action: StepAction = {
 *   label: t('common.next'),
 *   icon: ArrowRight,
 *   onClick: submit,
 *   testId: 'checkin-self-pay-next-btn',
 * }
 * ```
 */
export interface StepAction {
  /** Translated, plain-text label (a string keeps the publication stable). */
  readonly label: string
  /** Lucide icon component rendered before the label. Required: every kiosk button has an icon. */
  readonly icon: LucideIcon
  /** Invoked on activation. May change every render; the latest handler is always called. */
  readonly onClick: () => void
  /** `data-testid` of the footer button. */
  readonly testId: string
  /** Disables the action (e.g. form not yet valid). */
  readonly disabled?: boolean
  /** Shows a spinner in place of the icon and blocks activation while work is pending. */
  readonly busy?: boolean
  /** Visual tone; defaults to `primary`. */
  readonly tone?: StepActionTone
}

/**
 * A footer action as stored by the shell: the step's {@link StepAction}
 * description plus the publishing hook's owner id. The `onClick` is a stable
 * trampoline to the step's latest handler.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const { primaryAction } = useSelfService()
 * primaryAction?.onClick()
 * ```
 */
export interface PublishedStepAction extends StepAction {
  /** Identity of the publishing hook instance; used to clear only its own action. */
  readonly ownerId: string
}

type StepActionUpdate =
  | PublishedStepAction
  | null
  | ((current: PublishedStepAction | null) => PublishedStepAction | null)

/**
 * Product-level external input a self-service step may require from its host.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const input: SelfServiceExternalInput = 'health-card'
 * ```
 */
export type SelfServiceExternalInput = 'health-card'

/** A view title published to the global header by the active step. */
export interface ViewTitle {
  /** Heading content (usually the step's translated title). */
  readonly node: ReactNode
  /** Optional `data-testid` to apply to the header heading element. */
  readonly testId?: string
}

/**
 * Global footer step-progress, 1-based. Mirrors the handoff BotBar stepper.
 *
 * The minimal form `{ current, total }` renders "Schritt x / y". Flows can
 * describe progress more accurately with the optional fields:
 *
 * - `phases`: one short translated name per milestone (`phases.length === total`).
 *   The footer then names the phases ("Anmelden · Versicherung · Fertig") and
 *   highlights the current one instead of showing a bare counter (KC-02).
 * - `subStep`: progress inside the current phase, e.g. registration 2 of 3.
 *   The bar advances within the phase so long sub-flows no longer look stuck.
 * - `complete`: the flow's work is finished (e.g. the check-in was committed);
 *   every phase is shown as done and the bar is full.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const progress: StepProgress = {
 *   current: 1,
 *   total: 3,
 *   phases: [t('self-service.progress.identify'), t('self-service.progress.insurance'), t('self-service.progress.done')],
 *   subStep: { current: 2, total: 3 },
 * }
 * ```
 */
export interface StepProgress {
  /** 1-based index of the current step within the active (sub-)flow */
  readonly current: number
  /** total number of steps in the active (sub-)flow */
  readonly total: number
  /** Optional translated phase names, one per step (`length === total`). @since 1.3.0 */
  readonly phases?: readonly string[]
  /** Optional 1-based progress within the current phase. @since 1.3.0 */
  readonly subStep?: { readonly current: number; readonly total: number }
  /** Marks every phase as completed (bar full). @since 1.3.0 */
  readonly complete?: boolean
}

/**
 * Fraction (0..1) of the flow completed for a {@link StepProgress}: the
 * current phase counts as in progress, advanced by its `subStep` when given.
 * Without `subStep` the legacy `current / total` behaviour is kept.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * stepProgressFraction({ current: 2, total: 4 }) // 0.5
 * stepProgressFraction({ current: 2, total: 4, subStep: { current: 1, total: 2 } }) // 0.375
 * ```
 */
export function stepProgressFraction(progress: StepProgress): number {
  if (progress.total <= 0) return 0
  if (progress.complete === true) return 1
  const current = Math.min(Math.max(progress.current, 1), progress.total)
  const sub = progress.subStep
  if (sub === undefined || sub.total <= 0) return current / progress.total
  const subFraction = Math.min(Math.max(sub.current, 0), sub.total) / sub.total
  return (current - 1 + subFraction) / progress.total
}

const SelfServiceContext = createContext<SelfServiceContextValue | null>(null)

interface SelfServiceProviderProps {
  children: ReactNode
  onExitFlow: () => void
}

/**
 * Provider for self-service navigation context.
 * Allows flows to register their back handlers for unified navigation.
 */
export function SelfServiceProvider({ children, onExitFlow }: SelfServiceProviderProps) {
  const backHandlerRef = useRef<(() => void) | null>(null)
  const [hasActiveFlow, setHasActiveFlow] = useState(false)
  const [isBackHidden, setBackHidden] = useState(false)
  const [stepProgress, setStepProgress] = useState<StepProgress | null>(null)
  const [viewTitle, setViewTitle] = useState<ViewTitle | null>(null)
  const [externalInput, setExternalInput] =
    useState<SelfServiceExternalInput | null>(null)
  const [cardReadPending, setCardReadPending] = useState(false)
  const [terminalSessionLeaseActive, setTerminalSessionLeaseActive] = useState(false)
  const [primaryAction, setPrimaryAction] = useState<PublishedStepAction | null>(null)
  const [secondaryAction, setSecondaryAction] = useState<PublishedStepAction | null>(null)

  const registerBackHandler = useCallback((handler: () => void) => {
    backHandlerRef.current = handler
    setHasActiveFlow(true)
    return () => {
      backHandlerRef.current = null
      setHasActiveFlow(false)
      setBackHidden(false) // Reset when flow is unregistered
      setStepProgress(null) // Clear the footer stepper when the flow ends
      setViewTitle(null) // Clear the header title when the flow ends
      setExternalInput(null) // Clear external requirements when the flow ends
      setTerminalSessionLeaseActive(false)
      setPrimaryAction(null) // Clear footer actions when the flow ends
      setSecondaryAction(null)
    }
  }, [])

  const handleBack = useCallback(() => {
    if (backHandlerRef.current) {
      backHandlerRef.current()
    } else {
      onExitFlow()
    }
  }, [onExitFlow])

  const contextValue = useMemo<SelfServiceContextValue>(
    () => ({
      registerBackHandler,
      handleBack,
      hasActiveFlow,
      setBackHidden,
      isBackHidden,
      stepProgress,
      setStepProgress,
      viewTitle,
      setViewTitle,
      externalInput,
      setExternalInput,
      cardReadPending,
      setCardReadPending,
      terminalSessionLeaseActive,
      setTerminalSessionLeaseActive,
      primaryAction,
      secondaryAction,
      setPrimaryAction,
      setSecondaryAction
    }),
    [cardReadPending, externalInput, handleBack, hasActiveFlow, isBackHidden, primaryAction, registerBackHandler, secondaryAction, stepProgress, terminalSessionLeaseActive, viewTitle]
  )

  return (
    <SelfServiceContext.Provider value={contextValue}>
      {children}
    </SelfServiceContext.Provider>
  )
}

/**
 * Hook to access the self-service context
 */
export function useSelfService() {
  const context = useContext(SelfServiceContext)
  if (!context) {
    throw new Error('useSelfService must be used within SelfServiceProvider')
  }
  return context
}

/**
 * Like {@link useSelfService} but returns null outside a provider instead of
 * throwing. Lets shared step components (e.g. StepHeader) publish to the shell
 * when it's present, while still rendering standalone in isolated Storybook
 * stories that don't wrap SelfServiceProvider.
 */
export function useOptionalSelfService() {
  return useContext(SelfServiceContext)
}

/**
 * Publish a view title to the global header while the calling component is
 * mounted. No-ops outside a SelfServiceProvider (e.g. isolated Storybook
 * stories). Pass null to clear the header — used by hero/loading/terminal
 * screens that carry their own centered heading and have no StepHeader.
 */
export function useViewTitle(node: ReactNode | null, testId?: string) {
  const context = useContext(SelfServiceContext)
  // Depend on the stable `setViewTitle` setter, not the whole context value. The provider memoizes
  // its context value with `viewTitle` in the dependency list, so publishing a non-null title (a fresh
  // `{ node, testId }` object) changes `viewTitle` → recomputes the context value → new identity. If
  // this effect depended on `context`, that identity change would re-fire it and republish, looping
  // forever ("Maximum update depth exceeded"). The `useState` setter is referentially stable, so the
  // effect now runs only when the title actually changes.
  const setViewTitle = context?.setViewTitle
  useEffect(function publishViewTitle() {
    if (!setViewTitle) return
    setViewTitle(node != null ? { node, testId } : null)
  }, [setViewTitle, node, testId])
}

/**
 * Publish one product-neutral external-input requirement while a step is mounted.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * function CardStep() {
 *   useSelfServiceExternalInput('health-card', true)
 *   return <p>Insert card</p>
 * }
 * ```
 */
export function useSelfServiceExternalInput(
  input: SelfServiceExternalInput,
  active = true,
): void {
  const context = useContext(SelfServiceContext)
  const setExternalInput = context?.setExternalInput

  useEffect(function publishExternalInput() {
    if (!setExternalInput || !active) return
    setExternalInput(input)
    return function clearExternalInput() {
      setExternalInput(null)
    }
  }, [active, input, setExternalInput])
}

/**
 * Pauses kiosk inactivity reset while the mounted step awaits a bounded card read.
 *
 * @since 1.1.10
 * @example
 * ```tsx
 * function ReadingStep() {
 *   useSelfServiceCardReadPending(true)
 *   return <p>Reading</p>
 * }
 * ```
 */
export function useSelfServiceCardReadPending(pending: boolean): void {
  const context = useContext(SelfServiceContext)
  const setCardReadPending = context?.setCardReadPending
  useEffect(function publishCardReadPending() {
    if (!setCardReadPending || !pending) return
    setCardReadPending(true)
    return function clearCardReadPending() {
      setCardReadPending(false)
    }
  }, [pending, setCardReadPending])
}

/** Transfer shell timeout ownership to a bounded terminal interaction. @since 1.2.0 @example `useSelfServiceTerminalSessionLease(print.active);` */
export function useSelfServiceTerminalSessionLease(active: boolean): void {
  const context = useContext(SelfServiceContext)
  const setLeaseActive = context?.setTerminalSessionLeaseActive
  useEffect(function publishTerminalSessionLease() {
    if (!setLeaseActive || !active) return
    setLeaseActive(true)
    return function clearTerminalSessionLease() { setLeaseActive(false) }
  }, [active, setLeaseActive])
}

/**
 * Hook for flows to register their back handler.
 * The handler should navigate back within the flow or call onExitFlow when at start.
 *
 * @param handler - Function to handle back navigation (should be stable or memoized)
 */
export function useFlowBackHandler(handler: () => void) {
  const { registerBackHandler } = useSelfService()

  useEffect(function runContextEffect() {
    return registerBackHandler(handler)
  }, [registerBackHandler, handler])
}

type StepActionSlot = 'primary' | 'secondary'

function usePublishStepAction(slot: StepActionSlot, action: StepAction | null): void {
  const context = useContext(SelfServiceContext)
  const setAction =
    slot === 'primary' ? context?.setPrimaryAction : context?.setSecondaryAction
  const ownerId = useId()
  // The handler is read through a ref so a fresh closure on every render does
  // not republish the action (which would re-render the provider and loop).
  const onClickRef = useRef<(() => void) | null>(null)
  const onClick = action?.onClick ?? null
  useLayoutEffect(function syncLatestStepActionHandler() {
    onClickRef.current = onClick
  }, [onClick])
  const invoke = useCallback(function invokeLatestStepAction() {
    onClickRef.current?.()
  }, [])

  const label = action?.label
  const icon = action?.icon
  const testId = action?.testId
  const disabled = action?.disabled === true
  const busy = action?.busy === true
  const tone = action?.tone ?? 'primary'
  const present = action !== null

  useEffect(
    function publishStepAction() {
      if (!setAction || !present || label === undefined || icon === undefined || testId === undefined) {
        return
      }
      setAction({ label, icon, testId, disabled, busy, tone, onClick: invoke, ownerId })
      return function clearOwnStepAction() {
        setAction((current) => (current?.ownerId === ownerId ? null : current))
      }
    },
    [busy, disabled, icon, invoke, label, ownerId, present, setAction, testId, tone]
  )
}

/**
 * Publish the mounted step's primary (forward) action to the fixed footer
 * slot on the right, next to "Zurück" (KC-01, KO-08). The action is cleared
 * when the step unmounts or `null` is passed. Steps without a forward action
 * (pure choice tiles, terminal screens) simply do not call the hook, and the
 * footer renders no primary button. No-ops outside a SelfServiceProvider.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * function SelfPayStep() {
 *   const { t } = useTranslation()
 *   useStepPrimaryAction({
 *     label: t('common.next'),
 *     icon: ArrowRight,
 *     onClick: () => send({ type: 'SUBMIT_SELF_PAY' }),
 *     testId: 'self-pay-confirm',
 *   })
 *   return <KioskStep title={t('…title')}>…</KioskStep>
 * }
 * ```
 */
export function useStepPrimaryAction(action: StepAction | null): void {
  usePublishStepAction('primary', action)
}

/**
 * Publish an optional secondary action (e.g. "Nicht zustimmen", "Andere
 * Methode") to the footer, rendered as an outline button left of the primary
 * action at the same 56px height. Cleared on unmount.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * useStepSecondaryAction({
 *   label: t('self-service.check-in.eeb-consent.decline'),
 *   icon: X,
 *   onClick: decline,
 *   testId: 'eeb-consent-decline',
 * })
 * ```
 */
export function useStepSecondaryAction(action: StepAction | null): void {
  usePublishStepAction('secondary', action)
}
